import type { FC } from 'react';
import React, { Fragment } from 'react';
import { Box, Card } from '@edreamsodigeo/prisma-design-system';
import { withErrorBoundary } from '@edreamsodigeo/frontend-core';

import TitleHeaderMapItinerary from './components/TitleHeaderMapItinerary';
import SegmentMapItinerary from './components/SegmentMapItinerary';
import { BaggageConditionTextStyled } from './BaggageConditionTextStyled';
import SkeletonMapItinerary from './components/SkeletonMapItinerary';
import { useMetaFlowProvider } from '@/clientState/providers/MetaFlowProvider';
import type { MapItineraryProps, ConditionalWrapperProps } from './types';
import { BagsSeatsNegativeMarkupComponentAB } from './components/BagsSeatsNegativeMarkupComponentAB';
import { useItineraryData } from '@/clientState/hooks/useItineraryData';
import { filterProductDataByAncilliaries } from '@/clientState/commonFunctions/filterProductDataByAncilliaries';

const ItineraryWrapper: React.FC<ConditionalWrapperProps> = ({
  isMetaSummary,
  isFromDetails,
  children,
}) => {
  return isMetaSummary ? (
    <Box pt="base" px="base" mt={!isFromDetails ? '0' : 'minus_base'}>
      {children}
    </Box>
  ) : (
    <Box mt={!isFromDetails ? '0' : 'minus_base'}>
      <Card
        p="big"
        sx={{
          borderRadius: !isFromDetails ? '0' : 'big',
        }}
      >
        {children}
      </Card>
    </Box>
  );
};

export const MapItineraryComponent: FC<MapItineraryProps> = ({
  HotelDeals,
  bagsSeatsNegativeMarkup,
  justCabinBagDisclaimer = null,
  data,
  loading,
  isFromDetails = false,
  isMetaSummary = false,
}) => {
  const isMeta = useMetaFlowProvider();

  const { segments, isMultiDestination, isRoundTrip, destinationName } =
    useItineraryData(data);

  const { cabinBagsSelections } = filterProductDataByAncilliaries(data);
  const hasCabinBag = cabinBagsSelections.length > 0;
  const innerPaddingValue = isMetaSummary ? '2x_small' : '1x_small';

  return (
    <Box p={!isFromDetails ? innerPaddingValue : '0'}>
      {loading ? (
        <SkeletonMapItinerary isMetaSummary={isMetaSummary} />
      ) : (
        <>
          {!isFromDetails && !isMeta ? (
            <TitleHeaderMapItinerary segments={segments} />
          ) : null}
          <ItineraryWrapper
            isMetaSummary={isMetaSummary}
            isFromDetails={isFromDetails}
          >
            {segments.map((segment, idx) => (
              <Fragment
                key={`MapItinerary-${segment.flightCode}-${segment.id}`}
              >
                <SegmentMapItinerary
                  indx={idx}
                  segment={segment}
                  isMultiDestination={isMultiDestination}
                />
                <BaggageConditionTextStyled
                  baggageCondition={segment.baggageCondition}
                  hasCabinBag={hasCabinBag}
                />
                {!isFromDetails && !isMeta && (
                  <BagsSeatsNegativeMarkupComponentAB
                    {...{
                      HotelDeals,
                      bagsSeatsNegativeMarkup,
                      idx,
                      destinationName,
                      segments,
                      isRoundTrip,
                    }}
                  />
                )}
              </Fragment>
            ))}
            {justCabinBagDisclaimer}
          </ItineraryWrapper>
        </>
      )}
    </Box>
  );
};

export const MapItinerary = withErrorBoundary<MapItineraryProps>(
  MapItineraryComponent,
  { name: 'MapItinerary' }
);
